import type React from "react";
import styles from "./index.module.css";

// TypeScript interfaces for the data structure
export interface SuspectTag {
  userId: number;
  levelOfDeviation: string;
  riskLevel: "High" | "Medium" | "Low";
}

export interface Props {
  data: SuspectTag[];
  title?: string;
}

// Component to render risk level badges with appropriate colors
const RiskLevelBadge: React.FC<{ level: SuspectTag["riskLevel"] }> = ({
  level,
}) => {
  const getBadgeClass = (riskLevel: SuspectTag["riskLevel"]) => {
    switch (riskLevel) {
      case "High":
        return `${styles.riskBadge} ${styles.riskBadgeHigh}`;
      case "Medium":
        return `${styles.riskBadge} ${styles.riskBadgeMedium}`;
      case "Low":
        return `${styles.riskBadge} ${styles.riskBadgeLow}`;
      default:
        return `${styles.riskBadge} ${styles.riskBadgeLow}`;
    }
  };

  return <span className={getBadgeClass(level)}>{level}</span>;
};

// Main component
const SuspectTagsPageContent: React.FC<Props> = ({
  data,
  title = "User Risk Analysis",
}) => {
  return (
    <div className={styles.container}>
      {title && <h2 className={styles.title}>{title}</h2>}

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead className={styles.tableHead}>
            <tr>
              <th className={styles.tableHeader}>User ID</th>
              <th className={styles.tableHeader}>Level of deviation</th>
              <th className={styles.tableHeader}>Risk Level</th>
            </tr>
          </thead>
          <tbody className={styles.tableBody}>
            {data.map((user, index) => (
              <tr
                key={user.userId}
                className={`${styles.tableRow} ${
                  index % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd
                }`}
              >
                <td className={`${styles.tableCell} ${styles.tableCellUserId}`}>
                  {user.userId}
                </td>
                <td className={styles.tableCell}>{user.levelOfDeviation}</td>
                <td className={styles.tableCell}>
                  <RiskLevelBadge level={user.riskLevel} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.length === 0 && (
        <div className={styles.emptyState}>No user risk data available</div>
      )}
    </div>
  );
};

export default SuspectTagsPageContent;
